import { jsPDF } from 'jspdf';
import type { CycleSummary } from './billingCycles';
import { fmtUSD, fmtHours } from './billingCycles';
import { receiptFromTransaction, renderReceipt, receiptFilename } from './receipt';

const NAVY = '#0B1B3A';
const MUTED = '#67718a';
const M = 56; // page margin (pt)

function newDoc() {
  return new jsPDF({ unit: 'pt', format: 'letter' });
}

// Branded header band; returns the y to start content beneath it.
function header(doc: jsPDF, subtitle: string) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(NAVY);
  doc.rect(0, 0, w, 64, 'F');
  doc.setTextColor('#ffffff');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('AJM Aviation', M, 32);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor('#c9d6ec');
  doc.text(subtitle, M, 48);
  doc.setTextColor('#1f2a44');
  return 96;
}

function footer(doc: jsPDF) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  doc.text(
    'AJM Aviation Ventures, LLC · 101 Main Street, Ste H, Seal Beach, CA 90740',
    w / 2,
    h - 28,
    { align: 'center' }
  );
}

export function downloadStatementPdf(args: {
  cycle: CycleSummary;
  tailNumber: string;
  pilotName: string;
  cardLine?: string;
}) {
  const { cycle, tailNumber, pilotName } = args;
  const doc = newDoc();
  const w = doc.internal.pageSize.getWidth();
  let y = header(doc, `Billing statement · ${tailNumber}`);

  const period = `${cycle.start.toLocaleDateString()} – ${new Date(cycle.end.getTime() - 86400000).toLocaleDateString()}`;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(pilotName, M, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(MUTED);
  doc.text(`Billing period: ${period}`, M, y + 16);
  if (args.cardLine) doc.text(`Card on file: ${args.cardLine}`, M, y + 32);
  doc.setTextColor('#1f2a44');
  y += 56;

  // Line items
  const row = (label: string, value: string, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 13 : 11);
    doc.text(label, M, y);
    doc.text(value, w - M, y, { align: 'right' });
    y += bold ? 26 : 20;
  };
  doc.setDrawColor('#e6eaf0');
  row(`Hobbs hours — ${fmtHours(cycle.hoursFlown)} × ${fmtUSD(cycle.rate)}/hr`, fmtUSD(cycle.hoursCharge));
  row('Away-from-base charges', fmtUSD(cycle.awayCharge));
  doc.line(M, y - 6, w - M, y - 6);
  y += 8;
  row('Total this period', fmtUSD(cycle.totalCharged), true);
  y += 8;

  // Flights
  if (cycle.flights.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Flights this period', M, y);
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(MUTED);
    doc.text('Date', M, y);
    doc.text('Route', M + 80, y);
    doc.text('Hobbs', w - M - 120, y);
    doc.text('Hours', w - M, y, { align: 'right' });
    y += 6;
    doc.line(M, y, w - M, y);
    y += 14;
    doc.setTextColor('#1f2a44');
    for (const f of cycle.flights) {
      if (y > doc.internal.pageSize.getHeight() - 70) {
        footer(doc);
        doc.addPage();
        y = header(doc, `Billing statement · ${tailNumber}`);
      }
      doc.text(f.date, M, y);
      doc.text([f.departureAirport, ...f.destinations].join(' - ').slice(0, 40), M + 80, y);
      doc.text(`${f.hobbsOut.toFixed(1)} - ${f.hobbsIn.toFixed(1)}`, w - M - 120, y);
      doc.text(fmtHours(f.hoursFlown), w - M, y, { align: 'right' });
      y += 16;
    }
  }

  footer(doc);
  doc.save(`AJM-statement-${cycle.start.toISOString().slice(0, 7)}.pdf`);
}

export function downloadLeasePdf(args: {
  agreementText: string;
  signerName: string;
  signedAt?: string;
  version?: string;
  hash?: string;
}) {
  const doc = newDoc();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  let y = header(doc, 'Aircraft Dry Lease — executed copy');

  doc.setFontSize(9);
  doc.setTextColor(MUTED);
  const meta = [
    `Signed by: ${args.signerName}`,
    args.signedAt ? `Signed: ${args.signedAt}` : '',
    args.version ? `Version: ${args.version}` : '',
  ].filter(Boolean);
  doc.text(meta.join('   ·   '), M, y);
  y += 22;
  doc.setTextColor('#1f2a44');

  doc.setFont('times', 'normal');
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(args.agreementText, w - M * 2);
  for (const line of lines) {
    if (y > h - 70) {
      footer(doc);
      doc.addPage();
      y = header(doc, 'Aircraft Dry Lease — executed copy');
      doc.setFont('times', 'normal');
      doc.setFontSize(10);
    }
    doc.text(line, M, y);
    y += 13;
  }

  if (args.hash) {
    if (y > h - 90) {
      footer(doc);
      doc.addPage();
      y = header(doc, 'Aircraft Dry Lease — executed copy');
    }
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(MUTED);
    doc.text(`Integrity hash (SHA-256): ${args.hash}`, M, y);
  }

  footer(doc);
  doc.save(`AJM-signed-lease-${args.signerName.replace(/\s+/g, '-')}.pdf`);
}

// ───────────────────────── CFI checkout sign-off ─────────────────────────
// The filed evidence behind the owner's Named-Insured renter approval: what the
// CFI certified, when, and when the owner approved. Uploaded to the pilot's
// Documents on approval so it sits beside their certificate and medical.
import type { CheckoutDoc } from './types';
import { OWNER_LEGAL } from './identity';
import { TRACKED_REG } from './flightTrack';
import {
  CHECKOUT_FLIGHT_ITEMS,
  CHECKOUT_GROUND_ITEMS,
  CHECKOUT_IFR_ITEMS,
  CHECKOUT_LEGACY_ITEMS,
  CHECKOUT_NIGHT_ITEMS,
} from './types';

export function checkoutPdfBlob(c: CheckoutDoc, approvedAtText: string): Blob {
  const doc = newDoc();
  let y = header(doc, `CFI Checkout Sign-off · ${TRACKED_REG} · 1961 Cessna 172C`);
  const w = doc.internal.pageSize.getWidth();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor('#0B1B3A');
  doc.text(`${c.pilotName} — checked out by ${c.cfiName}`, M, y);
  y += 22;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor('#1f2a44');
  const privLabel = { vfr_day: 'Day VFR', night: 'Night', ifr: 'IFR' } as const;
  const meta = [
    `Checkout date: ${c.date}`,
    `Dual instruction given: ${c.dualHoursGiven.toFixed(1)} hr`,
    ...(typeof c.hobbs === 'number' ? [`Hobbs at completion: ${c.hobbs.toFixed(1)}`] : []),
    `Pilot certificate on file: ${c.pilotCertificate ?? 'Not recorded'}`,
    `CFI certificate: ${c.cfiCertNumber}`,
    `Privileges granted: ${(c.privileges ?? ['vfr_day']).map((p) => privLabel[p]).join(' · ')}`,
  ];
  meta.forEach((m) => {
    doc.text(m, M, y);
    y += 15;
  });
  y += 8;

  const pageBreak = () => {
    if (y > doc.internal.pageSize.getHeight() - 130) {
      footer(doc);
      doc.addPage();
      y = 60;
    }
  };
  const section = (title: string, items: string[]) => {
    pageBreak();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(title, M, y);
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    items.forEach((t) => {
      pageBreak();
      const lines = doc.splitTextToSize(`✓  ${t}`, w - M * 2);
      doc.text(lines, M, y);
      y += lines.length * 12 + 3;
    });
    y += 8;
  };
  // Fall through to the retired wording so a sign-off from before the item list
  // was rewritten still prints what the CFI actually signed.
  const label = (map: Record<string, string>, k: string) => map[k] ?? CHECKOUT_LEGACY_ITEMS[k] ?? k;
  section('Ground — covered and satisfactory', c.groundItems.map((k) => label(CHECKOUT_GROUND_ITEMS, k)));
  section(
    'Flight — demonstrated to the standard of the certificate held',
    c.flightItems.map((k) => label(CHECKOUT_FLIGHT_ITEMS, k))
  );
  if (c.nightItems?.length) {
    section('Night — covered and satisfactory', c.nightItems.map((k) => CHECKOUT_NIGHT_ITEMS[k] ?? k));
  }
  if (c.ifrItems?.length) {
    section('IFR — covered and satisfactory', c.ifrItems.map((k) => CHECKOUT_IFR_ITEMS[k] ?? k));
  }

  if (c.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text('CFI notes', M, y);
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    const lines = doc.splitTextToSize(c.notes, w - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 12 + 10;
  }

  doc.setDrawColor('#d7dbe4');
  doc.line(M, y, w - M, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const attest = doc.splitTextToSize(
    `CFI attestation: "I certify that I personally provided the ground and flight instruction above to ` +
      `this pilot in ${TRACKED_REG}, that the maneuvers were demonstrated to the standard of the certificate they ` +
      `hold, and that in my judgment they are competent to act as pilot in command of this aircraft for the ` +
      `privileges indicated: ${(c.privileges ?? ['vfr_day']).map((p) => privLabel[p]).join(', ')}. I hold a ` +
      `current flight instructor certificate and I am named and approved under AJM's insurance policy." ` +
      `— signed electronically by ${c.signatureName}, ${c.signedAt?.toDate?.().toLocaleString() ?? c.date}.`,
    w - M * 2
  );
  doc.text(attest, M, y);
  y += attest.length * 12 + 12;

  doc.setFont('helvetica', 'bold');
  const approval = doc.splitTextToSize(
    `Named-Insured approval: on the basis of the certification above, AJM Aviation Ventures LLC, as ` +
      `Named Insured, approved ${c.pilotName} as a renter pilot of ${TRACKED_REG} under the open-pilot provisions ` +
      `of its aircraft insurance policy. Approved by ${OWNER_LEGAL}, ${approvedAtText}.`,
    w - M * 2
  );
  doc.text(approval, M, y);

  footer(doc);
  return doc.output('blob');
}

// Receipt for a single ledger entry (card OR cash). The layout lives in
// src/lib/receipt.ts so scripts/receipt.mjs produces the identical document.
export function downloadReceiptPdf(args: {
  tx: Parameters<typeof receiptFromTransaction>[0]['tx'];
  flight?: Parameters<typeof receiptFromTransaction>[0]['flight'];
  payer: Parameters<typeof receiptFromTransaction>[0]['payer'];
  aircraft: Parameters<typeof receiptFromTransaction>[0]['aircraft'];
  cardLine?: string;
}) {
  const data = receiptFromTransaction(args);
  const doc = newDoc();
  renderReceipt(doc, data);
  doc.save(receiptFilename(data));
}
